import {
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	useState
} from "react";
import { DestinationWindow } from "client/Api/Apps/common";
import { ZenyaDocLiveInformationProvider } from "./provider";
import { LiveTileContent } from "../LiveTileContent";
import { TileTabProps } from "../TileTabSelector";
import { Guid } from "client_legacy/ndaw/guid";
import { never } from "client_legacy/ndaw/errors";
import { ZenyaDocResourceKind } from "client/Api/Workspace/LiveInformation/ZenyaDoc/zenyaDocLiveInformation";
import { Favorites } from "./Favorites";
import { BrowseDocuments } from "./BrowseDocuments";
import { TileContext } from "../TileContext";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { ZenyaDocTabDisplayModel } from "client/Api/Workspace/workspace";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type ZenyaDocLiveTileContentProps = {
	connectionId: number;
	destinationWindow: DestinationWindow;
	tabs: readonly ZenyaDocTabDisplayModel[];
	provider: ZenyaDocLiveInformationProvider;
	onNavigation(): void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const ZenyaDocLiveTileContent = (props: ZenyaDocLiveTileContentProps): ReactElement => {
	const {
		connectionId,
		destinationWindow,
		tabs,
		provider,
		onNavigation,
		onOpenTileMenu
	} = props;

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(tabs[0]?.uniqueId);

	const {
		id: tileId,
		name,
		icon,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const tabProps = useMemo(
		(): readonly TileTabProps[] => {
			return tabs.map(x => {
				const result: TileTabProps = {
					uniqueId: x.uniqueId,
					name: x.name
				};

				return result;
			});
		},
		[tabs]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigation();
		},
		[addAnalyticsEvent, onNavigation]
	);

	const activeTab: ZenyaDocTabDisplayModel | undefined = tabs.find(x => x.uniqueId === activeTabId);

	const renderActiveTab = (): ReactElement | null => {
		if (!activeTab) {
			return null;
		}

		switch (activeTab.resourceKind)
		{
			case ZenyaDocResourceKind.BrowseDocuments:
				return (
					<BrowseDocuments
						tabId={activeTab.uniqueId}
						connectionId={connectionId}
						destinationWindow={destinationWindow}
						provider={provider}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			case ZenyaDocResourceKind.Favorites:
				return (
					<Favorites
						tabId={activeTab.uniqueId}
						connectionId={connectionId}
						destinationWindow={destinationWindow}
						provider={provider}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			default:
				never(activeTab.resourceKind);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.TabSelector
					value={activeTabId}
					onChange={setActiveTabId}
					tabs={tabProps}
					tileId={tileId}
					tileName={name}
				/>
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{ renderActiveTab() }
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};
ZenyaDocLiveTileContent.displayName = "ZenyaDocLiveTileContent";
